import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BRANCHES, COMPANY } from "@/data/branches";
import { MapPin } from "lucide-react";
import { cityHref, stateHref } from "@/lib/urls";
import { useInstallCount } from "@/hooks/useInstallCount";

const FOOTER_LINKS = {
  Company: [
    { to: "/about", label: "About Smart Infovision" },
    { to: "/products", label: "Products" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
  ],
  Solutions: [
    { to: "/studio-experience", label: "Studio Experience Center", highlight: true },
    { to: "/studio-setup", label: "Studio Setup System" },
    { to: "/digital-board", label: "Digital Boards" },
    { to: "/smart-board", label: "Smart Boards" },
    { to: "/interactive-flat-panel", label: "Interactive Flat Panels" },
    { to: "/smart-classroom", label: "Smart Classroom Kits" },
    { to: "/online-teaching-setup", label: "Online Teaching Studio" },
  ],
  Resources: [
    { to: "/board-comparator", label: "AI Board Comparator" },
    { to: "/acoustic-calculator", label: "Acoustic Cost Calculator" },
    { to: "/recommendation", label: "AI Product Recommendation" },
    { to: "/quotation", label: "Instant Quotation (PDF)" },
    { to: "/branches", label: "17 Experience Centers" },
    { to: "/cities", label: "Service Cities" },
    { to: "/coverage-map", label: "India Coverage Heat Map" },
    { to: "/coachings", label: "Top Coaching Institutes" },
    { to: "/educators", label: "Top YouTube Educators" },
    { to: "/installations/timeline", label: "Installation Timeline" },
    { to: "/blog?category=comparison", label: "Buyer Guides" },
  ],
};

const SOCIAL = [
  { href: COMPANY.youtube, icon: "fa-brands fa-youtube", color: "#FF0000", label: "Studio Setup Channel" },
  { href: COMPANY.facebook, icon: "fa-brands fa-facebook", color: "#1877F2", label: "Facebook" },
  { href: COMPANY.instagram, icon: "fa-brands fa-instagram", color: "#E4405F", label: "Instagram" },
  { href: COMPANY.twitter, icon: "fa-brands fa-x-twitter", color: "#FFFFFF", label: "Twitter" },
  { href: COMPANY.whatsapp_link, icon: "fa-brands fa-whatsapp", color: "#25D366", label: "WhatsApp" },
];

export default function Footer() {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const { label: installCount } = useInstallCount();

  useEffect(() => {
    api.get("/locations/states").then((r) => setStates(r.data || []));
    api.get("/locations/cities").then((r) => setCities(r.data || []));
  }, []);

  return (
    <footer className="bg-black text-slate-300 mt-20" data-testid="main-footer">
      <div className="si-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-white rounded-lg p-1.5 inline-flex">
                <img
                  src="/logo.png"
                  alt="Smart Infovision"
                  className="h-9 w-auto"
                />
              </div>
              <div>
                <div className="text-white font-semibold text-lg tracking-tight" style={{ fontFamily: "Outfit" }}>
                  Smart Infovision
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#0047AB] font-semibold">
                  Digital Boards · Smart Class
                </div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              India&apos;s trusted partner for Interactive Flat Panels, Digital Boards and end-to-end Smart Classroom solutions. {installCount} installations across schools and coaching institutes pan-India.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <div>
                <i className="fa-solid fa-phone text-[#EA580C] mr-2" />
                <a href={`tel:${COMPANY.sales_phone}`} className="text-slate-300 hover:text-white" data-testid="footer-sales-phone">
                  Sales · {COMPANY.sales_phone}
                </a>
              </div>
              <div>
                <i className="fa-solid fa-headset text-[#EA580C] mr-2" />
                <a href={`tel:${COMPANY.service_phone}`} className="text-slate-300 hover:text-white" data-testid="footer-service-phone">
                  Service · {COMPANY.service_phone}
                </a>
              </div>
              <div>
                <i className="fa-solid fa-envelope text-[#EA580C] mr-2" />
                <a href={`mailto:${COMPANY.email}`} className="text-slate-300 hover:text-white" data-testid="footer-email">
                  {COMPANY.email}
                </a>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3" data-testid="footer-social">
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 rounded-full grid place-items-center bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <i className={`${s.icon} text-base text-white`} />
                </a>
              ))}
            </div>
          </div>

          {Object.entries(FOOTER_LINKS).map(([section, links]) => (
            <div key={section}>
              <h4 className="text-white font-semibold mb-4 tracking-tight" style={{ fontFamily: "Outfit" }}>{section}</h4>
              <ul className="space-y-2">
                {links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.to.replace(/[^a-z0-9]/gi, '-')}`}
                      className={
                        l.highlight
                          ? "text-sm font-semibold text-[#EA580C] hover:text-white transition-colors"
                          : "text-sm text-slate-400 hover:text-white transition-colors"
                      }
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* 17 Experience Centers */}
        <div className="mt-12 border-t border-white/10 pt-10" data-testid="footer-branches">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
            <div>
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#0047AB] font-semibold">Visit Us</span>
              <h4 className="text-white font-semibold text-xl mt-1 tracking-tight" style={{ fontFamily: "Outfit" }}>
                17 Experience Centers across India
              </h4>
            </div>
            <Link to="/branches" className="text-sm text-[#EA580C] hover:text-white font-semibold">
              See all centers →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {BRANCHES.map((b) => (
              <div
                key={b.slug}
                className="group p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#EA580C]/40 transition-all"
              >
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-[#EA580C] mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white">{b.city}</div>
                    <div className="text-[11px] text-slate-400 leading-relaxed line-clamp-2">{b.address}</div>
                    <div className="mt-1.5 flex items-center gap-3 text-[10px]">
                      <Link to={`/smart-infovision-${b.slug}/`}
                            data-testid={`footer-branch-${b.slug}`}
                            className="text-[#EA580C] font-semibold hover:underline">
                        Visit page →
                      </Link>
                      <a href={b.map} target="_blank" rel="noreferrer"
                         className="text-slate-300 hover:text-white">
                        Open in Maps ↗
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* State / City matrix */}
        <div className="mt-12 border-t border-white/10 pt-10">
          <h4 className="text-white font-semibold mb-4 tracking-tight" style={{ fontFamily: "Outfit" }}>States We Serve</h4>
          <div className="flex flex-wrap gap-2 mb-6">
            {states.map((s) => (
              <Link
                key={s.slug}
                to={stateHref(s.slug)}
                className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-[#0047AB] text-slate-300 hover:text-white border border-white/10 transition-colors"
              >
                {s.name}
              </Link>
            ))}
          </div>
          <h4 className="text-white font-semibold mb-4 tracking-tight" style={{ fontFamily: "Outfit" }}>Top Cities</h4>
          <div className="flex flex-wrap gap-2">
            {cities.slice(0, 40).map((c) => (
              <Link
                key={c.slug}
                to={cityHref(c.slug)}
                className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-[#EA580C] text-slate-400 hover:text-white border border-white/10 transition-colors"
              >
                Digital Board in {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Smart Infovision · All rights reserved. ·{" "}
        <a href={COMPANY.website} target="_blank" rel="noreferrer" className="hover:text-slate-300">{COMPANY.website.replace(/^https?:\/\//, "")}</a> ·{" "}
        <Link to="/admin/login" className="hover:text-slate-300">Admin</Link>
      </div>
    </footer>
  );
}
